import _ from 'underscore';
import Promise from 'bluebird';

import { isRadiusUnitMiles } from './radius-unit';

const RedundantTargetsType = {
  LEFT_IS_SUPERSET: -1,
  RIGHT_IS_SUPERSET: 1,
  NOT_REDUNDANT: null,
};

/**
 * Helper functions for accessing the Bing Maps V8 Web Control API.
 * These functions can be called without rendering the map control UI, useful for programmatically
 * making queries and performing map-related calculations.
 * See: https://docs.microsoft.com/en-us/bingmaps/v8-web-control/map-control-api/
 */
export class BingMapsApiHelper {
  constructor({
    bingMapsApiKey,
    Maps,
  }) {
    this.Maps = Maps;
    this.credentials = bingMapsApiKey;

    // used to cache the results of a redundant target validation
    this.redundantTargetsCache = {};
  }

  getPolygons(locationTarget, polygonOptions) {
    if (locationTarget.isRadiusLocationTarget) {
      return this.fetchCircle(locationTarget)
        .then(points => new this.Maps.Polygon(
          points,
          polygonOptions
        ));
    }

    return this.fetchLocationBoundary(locationTarget, polygonOptions).then((data) => {
      if (data && !_.isEmpty(data.results)) {
        return data.results[0].Polygons;
      }

      // no boundary polygon(s) found for this location
      return null;
    });
  }

  /**
   * Asynchronously loads the SpacialDataService module and fetches a boundary polygon for a given
   * location target. Memoized function to avoided repeated server requests.
   *
   * See: https://docs.microsoft.com/en-us/bingmaps/v8-web-control/modules/spatial-data-service-module/geodataapimanager-class
   * See: https://underscorejs.org/#memoize
   * @param {LocationTarget} locationTarget - LocationTarget instance that determines the search
   * parameters and the styling of the polygon.
   * @param {PolygonOptions} [polygonOptions] - Styles applied to the newly created polygons.
   * @returns {Promise} - Promise that resolves to the Bing Maps SDK response.
   */
  fetchLocationBoundary = _.memoize(
    (locationTarget, polygonOptions) => {
      const location = new this.Maps.Location(locationTarget.Latitude, locationTarget.Longitude);

      const getBoundaryRequestOptions = {
        entityType: locationTarget.getEntityType(),
        getAllPolygons: true,
      };

      return new Promise((resolve) => {
        this.Maps.loadModule('Microsoft.Maps.SpatialDataService', () => {
          this.Maps.SpatialDataService.GeoDataAPIManager.getBoundary(
            location,
            getBoundaryRequestOptions,
            this.credentials,
            resolve,
            polygonOptions
          );
        });
      });
    },
    // hashcode function to lookup if polygon is already in cache
    (locationTarget, polygonOptions) => `${locationTarget.getHashCode()},${JSON.stringify(polygonOptions)}`
  );

  fetchCircle(locationTarget) {
    const { radiusSize, radiusUnit } = locationTarget;
    const location = new this.Maps.Location(locationTarget.Latitude, locationTarget.Longitude);

    return new Promise((resolve) => {
      this.Maps.loadModule('Microsoft.Maps.SpatialMath', () => {
        const distanceUnit = isRadiusUnitMiles(radiusUnit) ?
          this.Maps.SpatialMath.DistanceUnits.Miles
          : this.Maps.SpatialMath.DistanceUnits.Kilometers;

        // create a polygon of 36 points in a circle around the latlon
        resolve(this.Maps.SpatialMath.getRegularPolygon(location, radiusSize, 36, distanceUnit));
      });
    });
  }

  /**
   * Checks if any of the location targets are a superset of another. This is needed because Facebook ad campaigns do
   * not allow redundant location targeting.
   *
   * @param {LocationTarget[]} locationTargets - List of location targets to test.
   * @returns {Boolean} - True if any of the location targets are redundant.
   */
  hasAnyRedundantTargets(locationTargets) {
    return this.getRedundantTargets(locationTargets, 1)
      .then(results => _.size(results) > 0);
  }

  /**
   * Examines a list of location targets and returns pairs of location targets where one is a
   * superset of the other. This is needed because Facebook ad campaigns do not allow redundant
   * location targeting like this.
   *
   * @param {LocationTarget[]} locationTargets - List of location targets to test.
   * @param {Number} [maxResults] - Maximum number of results to return.
   * @param {Boolean} [shouldUseFbRedundantTargetsCheck=false] - True if redundant targets should be checked according to the new logic.
   * @returns {Promise} - Promise that resolves to an array of pairs. First item is the superset and
   * second item is the subset.
   */
  getRedundantTargets(locationTargets, maxResults = null, shouldUseFbRedundantTargetsCheck = false) {
    const loadSpatialMath = new Promise((resolve) => {
      this.Maps.loadModule('Microsoft.Maps.SpatialMath', () => {
        resolve(true);
      });
    });

    // get polygons for every location target and then calculate all the pairs of supersets/subsets
    return loadSpatialMath
      .then(() => Promise.map(locationTargets, t => this.getPolygons(t)))
      .then((polygonSets) => {
        const comparisons = [];

        // compare each location target to every other location target to see if they are redundant
        for (let i = 0; i < polygonSets.length; i += 1) {
          for (let j = i + 1; j < polygonSets.length; j += 1) {
            const xTarget = locationTargets[i];
            const xPolygonSet = polygonSets[i];
            const yTarget = locationTargets[j];
            const yPolygonSet = polygonSets[j];

            comparisons.push([xTarget, xPolygonSet, yTarget, yPolygonSet]);
          }
        }

        // process each comparison asynchronously
        return Promise.reduce(comparisons, (memo, args) => {
          if (_.isFinite(maxResults) && _.size(memo) >= maxResults) {
            // skip further checks if maxResults has been reached
            return memo;
          }

          const [xTarget, xPolygonSet, yTarget, yPolygonSet] = args;

          const isRedundantTarget = shouldUseFbRedundantTargetsCheck ?
            Promise.resolve(this.isRedundantTarget2(xTarget, xPolygonSet, yTarget, yPolygonSet)) :
            this.isRedundantTargetAsync(xTarget, xPolygonSet, yTarget, yPolygonSet);

          return isRedundantTarget
            .then((result) => {
              if (result === RedundantTargetsType.LEFT_IS_SUPERSET) {
                memo.push([xTarget, yTarget]);
              } else if (result === RedundantTargetsType.RIGHT_IS_SUPERSET) {
                memo.push([yTarget, xTarget]);
              }
              return memo;
            });
        }, []);
      });
  }

  isRedundantTarget(xTarget, xPolygonSet, yTarget, yPolygonSet) {
    const cacheKey = `${xTarget.getHashCode()},${yTarget.getHashCode()}`;

    // check if result is in cache
    if (!_.has(this.redundantTargetsCache, cacheKey)) {
      // try to check redundancy based on parentlocations
      let result = this.calculateRedundantTargetBasedOnParentLocations(xTarget, yTarget);

      if (result == null
        && xPolygonSet != null
        && yPolygonSet != null
      ) {
        // compare x and y polygon sets
        const isIntersecting = this.Maps.SpatialMath.Geometry.intersects(xPolygonSet, yPolygonSet);

        if (isIntersecting) {
          if (this.Maps.SpatialMath.Geometry.contains(xPolygonSet, yPolygonSet)) {
            result = RedundantTargetsType.LEFT_IS_SUPERSET;
          } else if (this.Maps.SpatialMath.Geometry.contains(yPolygonSet, xPolygonSet)) {
            result = RedundantTargetsType.RIGHT_IS_SUPERSET;
          }
        }
      }

      // add the result to cache
      this.redundantTargetsCache[cacheKey] = result;
    }

    return this.redundantTargetsCache[cacheKey];
  }

  isRedundantTarget2(xTarget, xPolygonSet, yTarget, yPolygonSet) {
    const cacheKey = `${xTarget.getHashCode()},${yTarget.getHashCode()}`;

    // check if result is in cache
    if (!_.has(this.redundantTargetsCache, cacheKey)) {
      let result;

      if (!xTarget.isRadiusLocationTarget && !yTarget.isRadiusLocationTarget) {
        // x and y are both boundary targets, check redundancy based on ParentLocations property
        result = this.calculateRedundantTargetBasedOnParentLocations(xTarget, yTarget);
      } else {
        // one or both of x and y are radius targets, check redundancy based on radius logic
        result = this.calculateRedundantTargetForRadiusTarget(xTarget, xPolygonSet, yTarget, yPolygonSet);
      }

      // add the result to cache
      this.redundantTargetsCache[cacheKey] = result;
    }

    return this.redundantTargetsCache[cacheKey];
  }

  isRedundantTargetAsync(...args) {
    return new Promise((resolve) => {
      _.defer(() => {
        resolve(this.isRedundantTarget(...args));
      });
    });
  }

  /**
   * Optimized function that checks for redundancy based on parent locations. This check is much
   * faster than comparing polygon data but it is not exhaustive and it cannot handle radius target.
   * @param {LocationTarget} xTarget - Target to compare.
   * @param {LocationTarget} yTarget - Target to compare.
   * @returns {Number} - Undefined if no match in parent locations.
   */
  calculateRedundantTargetBasedOnParentLocations(xTarget, yTarget) {
    if (_.isFinite(xTarget.LocationId)
      && _.some(yTarget.ParentLocations, parent => parent.Id === xTarget.LocationId)) {
      return RedundantTargetsType.LEFT_IS_SUPERSET;
    }

    if (_.isFinite(yTarget.LocationId)
      && _.some(xTarget.ParentLocations, parent => parent.Id === yTarget.LocationId)) {
      return RedundantTargetsType.RIGHT_IS_SUPERSET;
    }

    return undefined;
  }

  /**
   * Calculate if a pair of targets are redundant, based on Facebook's location validation logic.
   * Facebook only considers a radius target redundant if the center of the radius target is inside a targeted city, state/region, or country.
   *
   * @param {LocationTarget} xTarget - Left target.
   * @param {*} xPolygonSet - Left polygon set.
   * @param {LocationTarget} yTarget - Right target.
   * @param {*} yPolygonSet - Right polygon set.
   * @returns {Number} - Number reflecting if the left target is a superset, or the right, or neither.
   */
  calculateRedundantTargetForRadiusTarget(xTarget, xPolygonSet, yTarget, yPolygonSet) {
    let radiusTarget;
    let boundaryTarget;
    let boundaryPolygonSet;

    if (xTarget.isRadiusLocationTarget && yTarget.isRadiusLocationTarget) {
      // if both are radius targets, they will always be valid
      return RedundantTargetsType.NOT_REDUNDANT;
    }

    if (xTarget.isRadiusLocationTarget) {
      radiusTarget = xTarget;
      boundaryTarget = yTarget;
      boundaryPolygonSet = yPolygonSet;
    } else {
      radiusTarget = yTarget;
      boundaryTarget = xTarget;
      boundaryPolygonSet = xPolygonSet;
    }

    // types of boundary locations that can be considered as parents of radius targets
    const parentLocationTypes = ['MetroArea', 'City', 'SubGeography', 'Country'];

    if (_.contains(parentLocationTypes, boundaryTarget.LocationType)) {
      // locations are considered redundant if the center of the radius location is inside the boundary location
      const radiusCenter = new this.Maps.Location(radiusTarget.Latitude, radiusTarget.Longitude);
      const isContaining = this.Maps.SpatialMath.Geometry.contains(boundaryPolygonSet, radiusCenter);

      if (isContaining) {
        return xTarget.isRadiusLocationTarget ? RedundantTargetsType.RIGHT_IS_SUPERSET : RedundantTargetsType.LEFT_IS_SUPERSET;
      }
    }

    // boundary location was not the right type, or did not contain the radius center
    return RedundantTargetsType.NOT_REDUNDANT;
  }
}
